// Rendering constants for the risk meter, one set per variant.
//
//   full - the grant detail chart, with outside labels, connectors and tooltips
//   icon - the portfolio grid cell: rings only, no text and no hover
//
// Each variant states its own pixel values and nothing is derived from the other. Scaling the
// 400px chart down to 32px put every line under a pixel, so the icon needs its own numbers.

export type RiskMeterVariant = 'full' | 'icon';

export interface RiskMeterVariantSettings {
    height: number;
    width: number;

    // Size of the center hole, as a fraction of the radius. At a third, the three bands - slices,
    // financial, center - each take a third of the radius.
    bandFraction: number;

    // ---- White space, in pixels ----------------------------------------------------------------
    // sliceSeparator - border stroke on every arc, which is what separates neighboring slices
    // innerGap       - between the center circle and the financial ring
    // outerGap       - between the financial ring and the outer slice ring
    //
    // These are not the white that ends up on screen, because the arc borders cover part of each
    // gap. With a border of b, what renders is:
    //
    //   slice to slice        b                (neighbors share an edge, the stroke straddles it)
    //   center to financial   innerGap + b/2   (the ring's stroke reaches inward, the disc has none)
    //   financial to slices   outerGap + b     (both rings reach into the space between them)
    //
    // So equal gaps means innerGap = b/2 and outerGap = 0, which is what the icon uses.
    //
    // outerGap is the space DevExtreme puts between the two series rings. It hardcodes 4px and
    // offers no option, so patches/devextreme+22.2.11.patch adds a seriesSpacing option to the pie
    // chart. Changing that patch means changing node_modules - restart the dev server, hot reload
    // will not pick it up.
    sliceSeparator: number;
    innerGap: number;
    outerGap: number;

    // ---- Text and marks ------------------------------------------------------------------------
    labelFontSize: number;
    labelBorderWidth: number;
    connectorWidth: number;
    // Stroke of the checkmark laid over a finished grant, in the 0-100 viewBox of the mark.
    completeMarkStrokeWidth: number;

    horizontalPadding: number;

    // Whether there is room for text: outside labels, connectors, slice tooltips and the center
    // popover.
    supportsText: boolean;

    // Whether a hovered slice is redrawn in its hover style. DevExtreme's default hover both
    // hatches the slice and drops its white border (generic.light gives pie hoverStyle a border of
    // visible: false), so the slice fattens by the border it lost. At 400px that reads as a
    // highlight; on the icon the whole shape is a few pixels across, so it just twitches. Off here
    // sets hoverMode: 'none' on both series, which masks the hover state out entirely.
    highlightOnHover: boolean;
}

// The 400px chart. Tuned by eye.
export const FullMeterSettings: RiskMeterVariantSettings = {
    height: 400,
    width: 400,
    bandFraction: 1 / 3,
    sliceSeparator: 2,
    innerGap: 2,
    outerGap: 0.5,
    labelFontSize: 16,
    labelBorderWidth: 3,
    connectorWidth: 1,
    completeMarkStrokeWidth: 10,
    horizontalPadding: 20,
    supportsText: true,
    highlightOnHover: true
};

// The portfolio grid icon. A 1px separator is the thinnest line that still reads at this size, and
// innerGap and outerGap are set so all three gaps render as that same 1px - see the border math
// above. An odd border makes DevExtreme shift the rings half a pixel; RiskMeterChart moves the
// center circle to match.
export const IconMeterSettings: RiskMeterVariantSettings = {
    height: 32,
    width: 32,
    bandFraction: 1 / 3,
    sliceSeparator: 1,
    innerGap: 0.5,
    outerGap: 0,
    // Not drawn - supportsText is false - but set so the variant is a complete description.
    labelFontSize: 8,
    labelBorderWidth: 1,
    connectorWidth: 1,
    completeMarkStrokeWidth: 14,
    horizontalPadding: 0,
    supportsText: false,
    highlightOnHover: false
};

const VariantSettings: Record<RiskMeterVariant, RiskMeterVariantSettings> = {
    full: FullMeterSettings,
    icon: IconMeterSettings
};

export function getVariantSettings(variant: RiskMeterVariant): RiskMeterVariantSettings {
    return VariantSettings[variant];
}
